import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Zap, Shield, BarChart3, CheckCircle, ArrowRight, FileText, PenTool, Hash, ShieldCheck, Download } from 'lucide-react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { PublicHeader } from '../components/PublicHeader';
import Footer from '../components/Footer';
import { HeroProductPreview } from '../components/HeroProductPreview';
import { TrustBar } from '../components/TrustBar';
import { ProblemSolutionSection } from '../components/ProblemSolutionSection';
import { FeatureGrid } from '../components/FeatureGrid';
import { HowItWorks } from '../components/HowItWorks';
import { UseCasesSection } from '../components/UseCasesSection';
import { GoogleReviewsSection } from '../components/GoogleReviewsSection';

export const LandingPage = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const { scrollY } = useScroll();
  
  // Parallax effect for hero section
  const heroY = useTransform(scrollY, [0, 500], [0, 150]);
  const heroOpacity = useTransform(scrollY, [0, 300], [1, 0.8]);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const features = [];

  const useCases = [];

  // Floating animation variants
  const floatingAnimation = {
    initial: { y: 0 },
    animate: {
      y: [-10, 10, -10],
      transition: {
        duration: 6,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  // Staggered fade-in for children
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };

  // Flying form animation - plays once
  const flyingFormVariants = {
    initial: { 
      x: 0, 
      y: 0, 
      scale: 1, 
      opacity: 0,
      rotate: 0 
    },
    flying: { 
      x: [0, -50, -150, -300, -450],
      y: [0, -80, -100, -60, 0],
      scale: [1, 1.1, 1.2, 1.1, 0.9],
      opacity: [0, 1, 1, 1, 0],
      rotate: [0, -5, -10, -5, 0],
      transition: {
        duration: 2,
        ease: "easeInOut",
        times: [0, 0.2, 0.5, 0.8, 1]
      }
    }
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Header */}
      <PublicHeader />

      {/* Hero Section with Animations */}
      <section className="relative overflow-hidden bg-gradient-to-b from-violet-100/80 via-white to-white py-12 lg:py-20">
        {/* Hero Background Image - Architectural */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/95976b65-1973-40cb-8fa6-a4827fe990ce/images/c79d244327e725be892aa70197885bb656b6f6434fbcfc9380fe5145fb21ebac.png" 
            alt="" 
            className="absolute inset-0 w-full h-full object-cover opacity-60"
            style={{ objectPosition: 'center center' }}
          />
          {/* Gradient overlay - fade to white at bottom */}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white" />
        </div>
        
        {/* Subtle animated elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{
              opacity: [0.1, 0.2, 0.1],
            }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-20 right-20 w-64 h-64 bg-violet-300 rounded-full blur-3xl"
          />
          <motion.div
            animate={{
              opacity: [0.1, 0.15, 0.1],
            }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            className="absolute bottom-20 left-20 w-64 h-64 bg-indigo-300 rounded-full blur-3xl"
          />
        </div>

        <motion.div 
          style={{ y: heroY, opacity: heroOpacity }}
          className="max-w-7xl mx-auto px-6 relative z-10"
        >
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
            {/* LEFT: copy + CTAs */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate={isLoaded ? "visible" : "hidden"}
              className="text-center lg:text-left max-w-xl mx-auto lg:mx-0"
            >
              <motion.h1
                variants={itemVariants}
                className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-slate-900 mb-5 tracking-tight leading-[1.05]"
                data-testid="hero-heading"
              >
                Build Smart Forms That{' '}
                <motion.span
                  className="bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent"
                  animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
                  transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
                  style={{ backgroundSize: "200% 200%" }}
                >
                  Automate Your Business
                </motion.span>
              </motion.h1>

              <motion.p
                variants={itemVariants}
                className="text-base sm:text-lg text-slate-600 mb-7 leading-relaxed"
                data-testid="hero-description"
              >
                Create powerful forms with conditional logic, approvals, workflows, integrations, and automated actions — all without coding.
              </motion.p>

              <motion.div
                variants={itemVariants}
                className="flex flex-col sm:flex-row items-center lg:justify-start justify-center gap-3 mb-6"
              >
                <Button size="lg" asChild className="rounded-full h-12 px-7 shadow-lg bg-indigo-600 hover:bg-[#F59E0B] text-white shadow-indigo-500/25 hover:shadow-xl hover:shadow-orange-500/30 transition-all duration-300" data-testid="hero-cta-btn">
                  <Link to="/signup" className="flex items-center space-x-2">
                    <span>Start Free</span>
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild className="rounded-full h-12 px-7 border-slate-300 hover:bg-[#F59E0B] hover:text-white hover:border-[#F59E0B] transition-all duration-300" data-testid="hero-try-btn">
                  <Link to="/try-builder">Try Builder Free</Link>
                </Button>
              </motion.div>

              <motion.ul
                variants={itemVariants}
                className="flex flex-wrap items-center justify-center lg:justify-start gap-x-5 gap-y-2 text-sm text-slate-600"
                data-testid="hero-trust-line"
              >
                <li className="flex items-center gap-1.5">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span>No coding required</span>
                </li>
                <li className="flex items-center gap-1.5">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span>Setup in minutes</span>
                </li>
                <li className="flex items-center gap-1.5">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span>Secure cloud platform</span>
                </li>
              </motion.ul>
            </motion.div>

            {/* RIGHT: animated product preview */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isLoaded ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="w-full max-w-[640px] mx-auto lg:mx-0 lg:ml-auto"
            >
              <HeroProductPreview />
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Section 2 — Trust Bar */}
      <TrustBar />

      {/* Section 3 — Problem / Solution */}
      <ProblemSolutionSection />

      {/* Section 4 — Modern Feature Grid */}
      <FeatureGrid />

      {/* Section 5 — How It Works (4 steps) */}
      <HowItWorks />

      {/* Section 6 — Use Cases */}
      <UseCasesSection />

      {/* Section 7 — Testimonials (real client proof) */}
      <GoogleReviewsSection />

      {/* Formlio Sign Section */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-600 via-violet-500 to-indigo-600" />
        </div>
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-4 py-1.5 rounded-full text-sm font-medium mb-4"
            >
              <PenTool className="w-4 h-4" />
              Formlio Sign
            </motion.div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-slate-900 mb-4">
              E-Signatures Built Into Your Forms
            </h2>
            <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
              Collect legally binding signatures, generate verified documents, and let anyone verify authenticity — all in one seamless flow.
            </p>
          </motion.div>

          {/* 4-step flow */}
          <div className="grid md:grid-cols-4 gap-6 mb-14">
            {[
              { step: '1', icon: FileText, title: 'Build Your Form', desc: 'Add a Signature field to any form in the drag-and-drop builder.', color: 'from-indigo-500 to-indigo-600' },
              { step: '2', icon: PenTool, title: 'Collect Signatures', desc: 'Users review the full document and sign electronically before submission.', color: 'from-violet-500 to-purple-600' },
              { step: '3', icon: Hash, title: 'Unique Document ID', desc: 'Every signed document gets a unique FL-SIGN number and SHA-256 integrity hash.', color: 'from-purple-500 to-indigo-600' },
              { step: '4', icon: Download, title: 'Download & Verify', desc: 'Signers download signed PDFs. Anyone can verify at formlio.app/verify.', color: 'from-indigo-600 to-violet-600' },
            ].map((item, idx) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
                className="text-center group"
              >
                <div className={`w-14 h-14 bg-gradient-to-br ${item.color} rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:shadow-xl transition-shadow`}>
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <div className="text-xs font-bold text-indigo-600 mb-1">STEP {item.step}</div>
                <h3 className="font-semibold text-slate-900 mb-1">{item.title}</h3>
                <p className="text-sm text-slate-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* Trust badges */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
            className="flex flex-wrap items-center justify-center gap-6 mb-10"
          >
            {[
              { icon: ShieldCheck, text: 'E-SIGN Act Compliant' },
              { icon: Shield, text: 'SHA-256 Document Hash' },
              { icon: CheckCircle, text: 'Public Verification' },
            ].map((badge, idx) => (
              <div key={idx} className="flex items-center gap-2 text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-full px-4 py-2">
                <badge.icon className="w-4 h-4 text-green-600" />
                <span className="font-medium">{badge.text}</span>
              </div>
            ))}
          </motion.div>

          {/* CTA row */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            viewport={{ once: true }}
            className="flex flex-wrap items-center justify-center gap-4"
          >
            <Button size="lg" asChild className="rounded-full h-12 px-8 bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg">
              <Link to="/formlio-sign">
                Learn More
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild className="rounded-full h-12 px-8">
              <Link to="/verify">Verify a Document</Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-indigo-600 to-violet-600 relative overflow-hidden">
        {/* Animated background pattern */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{
              x: [0, 100, 0],
              y: [0, -50, 0],
            }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="absolute top-0 left-0 w-full h-full opacity-10"
            style={{
              backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)",
              backgroundSize: "40px 40px"
            }}
          />
        </div>
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto px-6 text-center relative z-10"
        >
          <h2 className="text-3xl md:text-4xl lg:text-6xl font-heading font-bold text-white mb-6 tracking-tight leading-[1.05]" data-testid="cta-heading">
            Start Building Smarter{' '}
            <span className="bg-gradient-to-r from-amber-300 via-yellow-200 to-orange-200 bg-clip-text text-transparent">
              Workflows
            </span>{' '}
            Today
          </h2>
          <p className="text-lg sm:text-xl text-indigo-100 mb-10 max-w-2xl mx-auto leading-relaxed">
            Join thousands of teams who turned their forms into automated business outcomes — no coding, no glue scripts, no engineers required.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-6">
            <Button
              size="lg"
              asChild
              className="rounded-full h-14 px-9 text-base font-semibold shadow-2xl shadow-black/20 bg-white hover:bg-[#F59E0B] text-indigo-700 hover:text-white transition-all duration-300"
              data-testid="footer-cta-btn"
            >
              <Link to="/signup" className="flex items-center gap-2">
                <span>Start Free</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            </Button>
            <Button
              size="lg"
              asChild
              variant="outline"
              className="rounded-full h-14 px-9 text-base font-semibold bg-transparent border-2 border-white/40 text-white hover:bg-white/10 hover:border-white transition-all duration-300"
              data-testid="footer-cta-demo-btn"
            >
              <Link to="/contact">Schedule Demo</Link>
            </Button>
          </div>
          <p className="text-sm text-indigo-200/80">
            No credit card required · 14-day free trial · Cancel anytime
          </p>
        </motion.div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};
